import { LOCAL_STORAGE, TAB, WEBVIEW_TODO_TAB_INDEX } from 'constants/general';
import { PARAMS } from 'constants/params';
import {
  DASHBOARD_BASE_URL,
  PROJECT_ID_PATH_INDEX,
  PUBLIC_ROUTES,
  ROUTES,
  WEBVIEW_ROUTES
} from 'constants/routes';
import {
  ProjectDetailFocus,
  ProjectDetailTab,
  ProjectLifecycleStatus
} from 'enums';
import { match } from 'path-to-regexp';
import { getLocalStorage, getLocalStorageItems } from './helpers/storage';
import { isProduction } from './helpers';
import { t } from 'services/i18n.service';
import { DEFAULT_PROJECTS_PARAMS } from 'assets/data';

export const isPublicRoute = () => {
  const token = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);
  for (const routePattern of PUBLIC_ROUTES) {
    const matcher = match(routePattern, { decode: decodeURIComponent });
    if (matcher(window.location.pathname)) {
      return !token && true;
    }
  }

  return false;
};

export const getPathSegment = (index?: number) => {
  const segments = window.location.pathname.split('/').filter(Boolean);
  return segments[index || 1] ?? '';
};

export const checkIfRedirectNeeded = () => {
  const [
    redirectPath,
    packIdFromLocalStorage,
    focusTemplateIdFromLocalStorage
  ] = getLocalStorageItems([
    LOCAL_STORAGE.REDIRECT_PATH,
    LOCAL_STORAGE.PACK_ID,
    LOCAL_STORAGE.FOCUS_TEMPLATE_ID
  ]);

  let nextRoute = ROUTES.HOME;

  if (packIdFromLocalStorage) {
    nextRoute = ROUTES.HABIT_PACK_WELCOME;
  } else if (focusTemplateIdFromLocalStorage) {
    nextRoute = ROUTES.FOCUS_TEMPLATE_WELCOME;
  } else {
    nextRoute = redirectPath || ROUTES.HOME;
  }

  return nextRoute;
};

export const isCurrentPath = (path: string): boolean => {
  return window.location.pathname === path;
};

export const isCurrentPathIn = (routes: string[]) =>
  routes.some((route) => isCurrentPath(route));

export const getQueryParam = (key: string) =>
  new URLSearchParams(window.location.search).get(key);

export const removeQueryParam = (key: string) => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(key)) {
    return;
  }
  url.searchParams.delete(key);
  window.history.replaceState({}, '', url.toString());
};

export const doesPathMatchPatternStart = (pattern: string) => {
  const patternParts = pattern.split('/');
  const pathParts = window.location.pathname.split('/');

  if (pathParts.length < patternParts.length) {
    return false;
  }

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i];
    const pathPart = pathParts[i];

    if (!patternPart.startsWith(':') && patternPart !== pathPart) {
      return false;
    }
  }

  return true;
};

const isDynamicWebviewPath = [WEBVIEW_ROUTES.WEBVIEW_PROJECT_DETAIL].some(
  (basePath) => doesPathMatchPatternStart(`${basePath}/:id`)
);

const isWebviewRoute = Object.values(WEBVIEW_ROUTES).includes(
  window.location.pathname
);

const isStripeManageSubscriptionRedirect =
  getLocalStorage(LOCAL_STORAGE.REDIRECT_TO_MANAGE_SUBSCRIPTION_VIA_WEBVIEW) ===
  WEBVIEW_ROUTES.WEBVIEW_MANAGE_SUBSCRIPTION;

export const isRouteEmbedded =
  isWebviewRoute || isDynamicWebviewPath || isStripeManageSubscriptionRedirect;

export const getProjectDetailPath = (projectId: string): string =>
  isRouteEmbedded
    ? `${WEBVIEW_ROUTES.WEBVIEW_PROJECT_DETAIL}/${projectId}`
    : ROUTES.PROJECT_DETAIL.replace(':id', projectId);

export const getProjectSettingsDueDatePath = (projectId: string): string => {
  const params = new URLSearchParams({
    [PARAMS.TAB]: ProjectDetailTab.SETTINGS,
    [PARAMS.FOCUS]: ProjectDetailFocus.DUE_DATE
  });
  return `${getProjectDetailPath(projectId)}?${params.toString()}`;
};

export const getProjectNotePath = (
  projectId: string,
  noteId?: string
): string => {
  const params = new URLSearchParams({
    [PARAMS.TAB]: ProjectDetailTab.NOTES
  });
  if (noteId != null && noteId.length > 0) {
    params.set(PARAMS.NOTE_ID, noteId);
  }
  return `${getProjectDetailPath(projectId)}?${params.toString()}`;
};

export const getProjectsListPath = (): string =>
  isRouteEmbedded
    ? `${WEBVIEW_ROUTES.WEBVIEW_TODO_LIST}?${PARAMS.TAB}=${TAB.PROJECTS}`
    : ROUTES.PROJECTS;

export const getBrowseMorePacksPath = (): string =>
  isCurrentPath(WEBVIEW_ROUTES.WEBVIEW_SETTINGS)
    ? WEBVIEW_ROUTES.WEBVIEW_MARKETPLACE
    : ROUTES.MARKETPLACE;

export const getBrowseMorePacksUrl = (): string => {
  const baseUrl = isProduction
    ? DASHBOARD_BASE_URL.PROD
    : DASHBOARD_BASE_URL.DEV;
  return `${baseUrl}${getBrowseMorePacksPath()}`;
};

export const getProjectIdFromPath = (): string =>
  getPathSegment(
    isRouteEmbedded
      ? PROJECT_ID_PATH_INDEX.WEBVIEW
      : PROJECT_ID_PATH_INDEX.DASHBOARD
  );

export const navigateEmbeddedAware = (
  path: string,
  navigate: (to: string) => void
): void => {
  if (isRouteEmbedded) {
    window.location.assign(path);
    return;
  }
  navigate(path);
};

export const randomLoadingMessage = () => {
  const loadingMessages = [
    'lazy_load_placeholder.we_re_loading_greatness_for_you',
    'lazy_load_placeholder.final_touches_are_being_added_almost_ready',
    'lazy_load_placeholder.just_a_moment_good_things_take_a_second',
    'lazy_load_placeholder.securing_your_session'
  ];

  return t(loadingMessages[Math.floor(Math.random() * loadingMessages.length)]);
};

export const resolveTodoWebviewTabIndexFromUrl = (): number => {
  const tab = getQueryParam(PARAMS.TAB);
  if (tab === TAB.PROJECTS) {
    return WEBVIEW_TODO_TAB_INDEX.PROJECTS;
  }
  return WEBVIEW_TODO_TAB_INDEX.TASKS;
};

export const buildProjectsFetchParams = (
  search: string,
  status: ProjectLifecycleStatus = ProjectLifecycleStatus.ACTIVE
) => ({
  ...DEFAULT_PROJECTS_PARAMS,
  ...(search ? { search } : {}),
  // Active is the server default; only send the param when viewing other states.
  ...(status !== ProjectLifecycleStatus.ACTIVE ? { status } : {})
});
